import { selectTakes } from "../../../app/slices/TrainingSlice";
import { OnboardingTarget } from "../onboardingTargets";
import type {
  OnboardingStepDefinition,
  OnboardingTour,
} from "../onboardingTypes";

/**
 * The 3D orientation tour.
 *
 * The empty-project constraint bites harder here than in 360. When a 3D
 * project has no takes, `show3DEmptyHero` (TrainingEditor.tsx:82-85) replaces
 * the ENTIRE editor body with TrainingEditor3DEmpty — storyboard, scenario
 * builder, viewport and properties are all absent from the DOM. So stage A can
 * only anchor to the hero itself.
 *
 * Unlike 360, no steps are shared between the stages: there is no overlap
 * between "what exists when empty" and "what exists once populated".
 *
 * See docs/ONBOARDING_REDESIGN_PLAN.md §4.2.
 */

const hasTakes = (state: any) => (selectTakes(state) ?? []).length > 0;

const K = "onboarding.tour.editor3D";

const step = (
  target: OnboardingStepDefinition["target"],
  name: string,
  placement: OnboardingStepDefinition["placement"],
): OnboardingStepDefinition => ({
  target,
  titleKey: `${K}.${name}.title`,
  bodyKey: `${K}.${name}.body`,
  placement,
});

/**
 * Empty project: the hero is literally the only thing rendered.
 *
 * TEMPORARILY DISABLED — not listed in `editor3DTours` below, so the host never
 * offers it. A one-step tour on a screen that already explains itself was not
 * earning its interruption. The definition and its five translations are kept
 * intact; re-enable by putting it back in the array.
 *
 * Note its predicate is also narrower than it looks: `show3DProjectSettings`
 * takes priority over `show3DEmptyHero` (TrainingEditor.tsx:210), so the hero
 * is absent on the Project Settings tab even with zero takes. That wants fixing
 * before this goes back in.
 */
export const editor3DStageA: OnboardingTour = {
  id: "editor3DStageA",
  canRun: (state) => !hasTakes(state),
  steps: [step(OnboardingTarget.Editor3DEmptyHero, "start", "bottom")],
  progressSegment: () => ({
    startIndex: 0,
    labelKey: `${K}.progress.editor`,
  }),
};

/**
 * Has takes: the editor proper.
 *
 * The tab rail step carries the single biggest 3D conceptual hurdle — actors
 * and props are project-wide libraries, and placing one in a scene is a second,
 * separate action.
 */
export const editor3DStageB: OnboardingTour = {
  id: "editor3DStageB",
  canRun: hasTakes,
  steps: [
    step(OnboardingTarget.Editor3DStoryboard, "storyboard", "right"),
    step(OnboardingTarget.Editor3DScenarioBuilder, "scenario_builder", "right"),
    step(OnboardingTarget.Editor3DTabRail, "tab_rail", "right"),
    step(OnboardingTarget.Editor3DViewport, "viewport", "left"),
    step(OnboardingTarget.ActionbarTestButton, "test", "bottom"),
  ],
  progressSegment: () => ({
    startIndex: 0,
    labelKey: `${K}.progress.editor`,
  }),
};

/** editor3DStageA is deliberately absent — see the note on its definition. */
export const editor3DTours: OnboardingTour[] = [editor3DStageB];
